import { z } from 'zod';
import { CentsSchema, FxMicroSchema } from './money.js';
/**
 * CSP promotions — customer discounts, and what they cost Exigo in lost MCI.
 *
 * A promotion cuts what the customer pays; an MCI incentive is what Microsoft
 * pays Exigo. They are never added together. They meet in exactly one place:
 * engagements sized on annual contract value are measured on the *discounted*
 * price, so a percentage discount can push a deal down a band and cost more in
 * lost incentive than it saves the customer.
 *
 * **Confidentiality boundary.** `BpBand` carries MCI payout figures and must
 * never reach the browser — see security.md §14. The catalogue below (names,
 * discounts, dates, quantity limits) is CSP pricing information the legacy tool
 * displayed openly, and is safe to serve. The band arithmetic that consumes
 * `BpBand` runs server-side and only its conclusions are sent.
 */
/** Promotion families, each mapping to a different MCI interaction. */
export const PromoFamily = z.enum([
    'd365',
    'me3',
    'me5',
    'copilot',
    'copbiz',
    'w365',
    'purview',
    'agent',
    /** Unrecognised offer name from a CSV refresh. Never assumed harmless. */
    'other',
]);
/**
 * How a discount in this family collides with MCI.
 * - `band`   — sizes on contract value, so the discount can drop a band.
 * - `attain` — band holds, but revenue-based performance goals get harder.
 * - `safe`   — sizes on seats or has no engagement: pure customer saving.
 * - `unknown` — unmapped. Check manually rather than assuming no effect.
 */
export const PromoRisk = z.enum(['band', 'attain', 'safe', 'unknown']);
/**
 * `other` exists for CSV refreshes only. Partner Center exports carry segments
 * the guide prices no engagements for — government, for one — and dropping those
 * rows would make the promotion count disagree with the file just uploaded. They
 * appear only under the "Show all" filter.
 */
export const PromoSegment = z.enum(['commercial', 'education', 'nonprofit', 'other']);
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO yyyy-mm-dd date');
/**
 * The sentinel the Partner Center export uses for "no maximum". Kept as the
 * legacy value rather than switched to null: the extract carries it verbatim and
 * the quantity-limit wording depends on recognising it.
 */
export const PROMO_QTY_UNLIMITED = 10_000_000;
export const Promotion = z
    .object({
    /**
     * Stable identity for a promotion row, derived from the fields that make it
     * distinct. The Partner Center export has no id of its own and groups many
     * SKUs into one promotion, so this is composed rather than supplied.
     */
    id: z.string().min(1),
    name: z.string().min(1),
    family: PromoFamily,
    discountPct: z.number().min(0).max(100),
    termMonths: z.number().int().min(1),
    startDate: IsoDate,
    endDate: IsoDate,
    segment: PromoSegment,
    /** How many SKU rows in the export collapsed into this promotion. */
    skuCount: z.number().int().min(1),
    qtyMin: z.number().int().min(0),
    qtyMax: z.number().int().min(0),
})
    .strict();
/** Per-family guidance. Safe to serve — prose, not rates. */
export const PromoMciInfo = z
    .object({
    risk: PromoRisk,
    basis: z.enum(['acv', 'seats', 'none']),
    /** Which MCI engagement(s) the family collides with, or "none". */
    engagement: z.string(),
    note: z.string(),
})
    .strict();
/**
 * A Business Processes CSP band. **Server-side only — contains MCI payouts.**
 * Used to price the band-drop risk a discount creates.
 */
export const BpBand = z
    .object({
    label: z.string().min(1),
    floorUsd: CentsSchema,
    payoutUsd: CentsSchema,
})
    .strict();
/** The promotion catalogue as extracted from one guide export. */
export const PromoCatalogue = z
    .object({
    asAt: z.string().min(1),
    promotions: z.array(Promotion),
    mci: z.record(PromoFamily, PromoMciInfo),
})
    .strict();
// --- Input ------------------------------------------------------------------
export const PromoLineInput = z
    .object({
    promotionId: z.string().min(1),
    qty: z.number().int().min(0).max(100_000_000),
    /** Sell price per seat per month, in cents. 0 means not entered. */
    priceCents: CentsSchema,
})
    .strict();
export const PromoScenarioInput = z
    .object({
    schemaVersion: z.literal(1),
    /** Which currency the entered prices are in. */
    currency: z.enum(['AUD', 'USD']),
    /** AUD per 1 USD, micro-units. 0 = unset, which blocks the band test. */
    fx: FxMicroSchema,
    segmentFilter: z.enum(['commercial', 'education', 'nonprofit', 'all']),
    /**
     * Hides promotions that are expired or not yet open. This is not purely
     * cosmetic: hidden rows drop out of the totals and the impact panel too,
     * matching the legacy tool.
     */
    hideExpired: z.boolean(),
    lines: z.array(PromoLineInput),
})
    .strict();
export const promoScenarioDefaults = {
    schemaVersion: 1,
    currency: 'AUD',
    fx: 0,
    segmentFilter: 'commercial',
    hideExpired: true,
    lines: [],
};
// --- Result -----------------------------------------------------------------
export const PromoStatus = z
    .object({
    kind: z.enum(['not_open', 'expired', 'ending', 'live']),
    /** Short badge text, e.g. "Ending". */
    label: z.string(),
    /** Date line under the badge, e.g. "to 30 Sep 2026". */
    sub: z.string(),
    /** Sentence fragment for the impact panel. */
    prose: z.string(),
    /** Whole days from today to the end date. Negative once expired. */
    days: z.number().int(),
    /** Whether the promotion can be sold today. */
    ok: z.boolean(),
})
    .strict();
export const PromoLineResult = z
    .object({
    promotionId: z.string(),
    qty: z.number().int(),
    priceCents: CentsSchema,
    grossYr: CentsSchema,
    saveYr: CentsSchema,
    netYr: CentsSchema,
    /** Null when no quantity was entered — not yet a pass or a fail. */
    qtyOk: z.boolean().nullable(),
    /** Counts toward the totals: priced, in range, and currently sellable. */
    active: z.boolean(),
    status: PromoStatus,
    risk: PromoRisk,
    /** What the MCI effect column shows. */
    effectLabel: z.string(),
    /**
     * Set when a row-level problem overrides the family risk — a quantity
     * outside the promotion's own limits, or a promotion not currently open.
     */
    effectOverride: z.enum(['qty_outside', 'not_available']).nullable(),
    /** Human-readable quantity limit, e.g. "up to 300 seats". */
    qtyLimitText: z.string(),
})
    .strict();
/** The band-drop conclusion. Derived from BpBand server-side; carries no bands. */
export const PromoBandImpact = z
    .object({
    /** Band before the discount, null when below the smallest band. */
    bandFrom: z.string().nullable(),
    bandTo: z.string().nullable(),
    /** USD cents of MCI given up by dropping a band. 0 when the band holds. */
    lostUsd: CentsSchema,
    discountedAcvUsd: CentsSchema,
    /** The discount that produced these zones. */
    discountPct: z.number(),
    /**
     * Gross contract-value ranges that lose a band once discounted, with what
     * each costs. Above each range the discount is free.
     */
    zones: z.array(z.object({ fromUsd: CentsSchema, toUsd: CentsSchema, losesUsd: CentsSchema }).strict()),
})
    .strict();
export const PromoImpact = z
    .object({
    customerSavesYr: CentsSchema,
    customerPaysYr: CentsSchema,
    grossYr: CentsSchema,
    activeCount: z.number().int().min(0),
    /** Per-family guidance for every family with an active line. */
    familyNotes: z.array(z.object({ family: PromoFamily, name: z.string(), note: z.string() }).strict()),
    /** Present only when a contract-value-sized family (D365) is in play. */
    band: PromoBandImpact.nullable(),
    /**
     * True when a band test was wanted but AUD prices have no FX rate to
     * convert. The legacy tool showed a dash rather than guessing a rate.
     */
    bandNeedsFx: z.boolean(),
})
    .strict();
export const PromoResult = z
    .object({
    guideVersion: z.string(),
    asAt: z.string(),
    /** Total promotions in the catalogue, before filtering. */
    promotionCount: z.number().int().min(0),
    /** One entry per *visible* promotion, in catalogue order. */
    lines: z.array(PromoLineResult),
    totals: z
        .object({
        activeCount: z.number().int().min(0),
        grossYr: CentsSchema,
        saveYr: CentsSchema,
        netYr: CentsSchema,
    })
        .strict(),
    /** Null until at least one line is active. */
    impact: PromoImpact.nullable(),
})
    .strict();
/**
 * Composes a promotion's stable id from the fields that make it distinct.
 *
 * **Divergence from legacy.** The legacy `promoRowId` omitted the offer name, and
 * two FY27 promotions collide without it: "Windows 365 Frontline" and
 * "Windows 365 add-ons (DR, cross-region)" share a family, discount, term, end
 * date, segment and quantity limits. Because the legacy tool keyed its entered
 * quantities and prices on that id, typing a quantity against one silently filled
 * the other and the totals counted both — overstating the customer saving.
 * The name is included here so each row is its own. See DIVERGENCES.md.
 */
export function promotionId(p) {
    return [
        p.family,
        p.discountPct,
        p.endDate,
        p.termMonths,
        p.segment,
        p.qtyMin,
        p.qtyMax,
        p.name,
    ].join('~');
}
/** "any quantity" / "5+ seats" / "up to 300 seats" / "5–300 seats". */
export function qtyLimitText(qtyMin, qtyMax) {
    if (qtyMin <= 1 && qtyMax >= PROMO_QTY_UNLIMITED)
        return 'any quantity';
    if (qtyMax >= PROMO_QTY_UNLIMITED)
        return `${String(qtyMin)}+ seats`;
    if (qtyMin <= 1)
        return `up to ${String(qtyMax)} seats`;
    return `${String(qtyMin)}–${String(qtyMax)} seats`;
}
/** "1 year" / "3 years" / "1 month" — the promotion's own commitment term. */
export function promoTermText(termMonths) {
    if (termMonths % 12 === 0) {
        const years = termMonths / 12;
        return `${String(years)} year${years > 1 ? 's' : ''}`;
    }
    return `${String(termMonths)} month${termMonths > 1 ? 's' : ''}`;
}
//# sourceMappingURL=promotions.js.map